import React, { useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { REPORT_PAGE_META, REPORT_DOCUMENT_TYPES } from '../../utils/reportDocumentTypes';
import ReportFiltersBar from './ReportFiltersBar';
import StudentSelectTable from './StudentSelectTable';
import DocumentPreviewModal from './DocumentPreviewModal';
import IdCardPreviewModal from './IdCardPreviewModal';
import BulkDownloadPanel from './BulkDownloadPanel';
import TcEditModal from './TcEditModal';
import { useReportStudents, filterStudentsBySearch, useStudentSelection } from './useReportStudents';
import { useBulkDownload } from './useBulkDownload';
import api, { getProfileAssetUrl } from '../../utils/api';
import { todayString } from '../../utils/appDateTime';
import { useToast } from '../../hooks/use-toast';
import { canManageFeature } from '../../utils/featurePermissions';
import { findTemplateById, parseIdCardTemplate } from '../../utils/idCardFormats';
import { handleBlobDownloadResponse } from '../../utils/blobDownload';
import { isNativePlatform } from '../../utils/device';
import { downloadIdCardPdf, printIdCard } from '../../utils/idCardExportService';
import IdCardSheetPreviewModal from './IdCardSheetPreviewModal';
import IdCardPrintSettingsPanel from './IdCardPrintSettingsPanel';
import { idCardImageCacheKey } from '../../utils/idCardCardData';
import { DEFAULT_PRINT_SETTINGS, mergePrintSettings } from '../../utils/idCardPrintSettings';

function defaultFilters() {
  return {
    classId: '',
    sectionId: '',
    academicYearId: '',
    status: 'active',
    examId: '',
    mode: 'exam',
    cardFormat: 'portrait',
    sheetOrientation: 'portrait',
    includeBack: true,
  };
}

export default function ReportPageShell({ documentType }) {
  const meta = REPORT_PAGE_META[documentType] || {};
  const { user } = useAuth();
  const { toast } = useToast();
  const schoolId = user?.school_id;
  const canBulk = canManageFeature(user, 'reports');

  const [filters, setFilters] = useState(defaultFilters);
  const [search, setSearch] = useState('');
  const [certificateFields, setCertificateFields] = useState({ purpose: '', issue_date: '', body_text: '' });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewParams, setPreviewParams] = useState(null);
  const [tcStudent, setTcStudent] = useState(null);
  const [tcOpen, setTcOpen] = useState(false);
  const [activeTemplateId, setActiveTemplateId] = useState('');
  const [activeIdCardTemplateId, setActiveIdCardTemplateId] = useState('');
  const [idCardTemplates, setIdCardTemplates] = useState([]);
  const [sheetPreviewOpen, setSheetPreviewOpen] = useState(false);
  const [idCardPreviewStudent, setIdCardPreviewStudent] = useState(null);
  const [idCardSchool, setIdCardSchool] = useState(null);
  const [idCardSchoolLoading, setIdCardSchoolLoading] = useState(false);
  const [idCardSchoolError, setIdCardSchoolError] = useState('');
  const [printSettings, setPrintSettings] = useState(DEFAULT_PRINT_SETTINGS);
  const [savingPrintSettings, setSavingPrintSettings] = useState(false);

  const activeIdCardTemplate = useMemo(
    () => findTemplateById(idCardTemplates, activeIdCardTemplateId),
    [idCardTemplates, activeIdCardTemplateId],
  );
  const idCardTemplateMeta = useMemo(
    () => parseIdCardTemplate(activeIdCardTemplate),
    [activeIdCardTemplate],
  );

  const isIdCardPage = documentType === REPORT_DOCUMENT_TYPES.ID_CARD;
  const allowAllClasses = Boolean(meta.allowAllClasses);
  const needsClass = !allowAllClasses;

  const isTcPage = documentType === REPORT_DOCUMENT_TYPES.TC;

  const { students, classIsTwelve, loading, error, refetch } = useReportStudents({
    schoolId,
    classId: filters.classId,
    sectionId: filters.sectionId,
    academicYearId: filters.academicYearId,
    status: filters.status,
    enabled: Boolean(schoolId && (filters.classId || allowAllClasses)),
    allowAllClasses,
    includeFee: isTcPage,
  });

  const filtered = useMemo(() => filterStudentsBySearch(students, search), [students, search]);
  const { selectedIds, toggleOne, toggleAll, allSelected, clearSelection } = useStudentSelection(filtered);
  const { runBulk, progress, running, error: bulkError } = useBulkDownload(documentType);

  React.useEffect(() => {
    if (documentType !== REPORT_DOCUMENT_TYPES.CERTIFICATE || !schoolId) return;
    api.get('/reports/templates', { params: { school_id: schoolId, category: 'certificate' } })
      .then((res) => setActiveTemplateId(res.data?.data?.active_template_id || ''))
      .catch(() => {});
  }, [documentType, schoolId]);

  const loadIdCardTemplates = React.useCallback(() => {
    if (documentType !== REPORT_DOCUMENT_TYPES.ID_CARD || !schoolId) return;
    api.get('/reports/templates', { params: { school_id: schoolId, category: 'id_card' } })
      .then((res) => {
        const data = res.data?.data || {};
        const templates = data.templates || [];
        const activeId = data.active_template_id || '';
        setIdCardTemplates(templates);
        setActiveIdCardTemplateId(activeId);
        const active = findTemplateById(templates, activeId) || templates[0];
        if (active) {
          const { format: fmt } = parseIdCardTemplate(active);
          setFilters((prev) => ({
            ...prev,
            cardFormat: fmt,
          }));
        }
      })
      .catch(() => {});
  }, [documentType, schoolId]);

  React.useEffect(() => {
    loadIdCardTemplates();
  }, [loadIdCardTemplates]);

  React.useEffect(() => {
    if (!isIdCardPage || !schoolId) return undefined;
    let cancelled = false;
    api.get('/reports/id-card/print-settings', { params: { school_id: schoolId } })
      .then((res) => {
        if (!cancelled) {
          const saved = mergePrintSettings(res.data?.data?.settings || {});
          setPrintSettings((prev) => mergePrintSettings({
            ...saved,
            cardFormat: idCardTemplateMeta.format || prev.cardFormat || saved.cardFormat,
          }));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isIdCardPage, schoolId, idCardTemplateMeta.format]);

  React.useEffect(() => {
    if (!isIdCardPage) return;
    setPrintSettings((prev) => mergePrintSettings({
      ...prev,
      cardFormat: filters.cardFormat || 'portrait',
    }));
  }, [isIdCardPage, filters.cardFormat]);

  React.useEffect(() => {
    if (documentType !== REPORT_DOCUMENT_TYPES.ID_CARD) return undefined;
    const onFocus = () => loadIdCardTemplates();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [documentType, loadIdCardTemplates]);

  React.useEffect(() => {
    if (!isIdCardPage || !schoolId) {
      setIdCardSchool(null);
      setIdCardSchoolLoading(false);
      setIdCardSchoolError('');
      return undefined;
    }
    let cancelled = false;
    setIdCardSchoolLoading(true);
    setIdCardSchoolError('');
    api.get(`schools/${schoolId}`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data ?? null;
        setIdCardSchool(data);
        if (!data) {
          setIdCardSchoolError('School data not found. Refresh the page or check your permissions.');
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setIdCardSchool(null);
          setIdCardSchoolError(
            e?.response?.data?.message || 'Could not load school ID card settings. Refresh and try again.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIdCardSchoolLoading(false);
      });
    return () => { cancelled = true; };
  }, [isIdCardPage, schoolId]);

  const buildParams = (student) => {
    const p = {
      student_id: student.id,
      school_id: schoolId,
    };
    if (filters.academicYearId) p.academic_year_id = filters.academicYearId;
    if (meta.showExamMode) {
      p.mode = filters.mode || 'exam';
      if (filters.examId && filters.mode !== 'annual') p.exam_id = filters.examId;
    }
    if (meta.showCertificateFields) {
      p.certificate_fields = JSON.stringify({
        purpose: certificateFields.purpose || 'Certificate',
        issue_date: certificateFields.issue_date || todayString(),
        body_text: certificateFields.body_text || '',
      });
      if (activeTemplateId) p.template_id = activeTemplateId;
    }
    if (meta.showCardFormat) {
      p.sheet_orientation = filters.sheetOrientation || 'portrait';
      p.include_back = filters.includeBack !== false ? '1' : '0';
      p.card_format = filters.cardFormat || 'portrait';
      if (activeIdCardTemplateId) p.template_id = activeIdCardTemplateId;
      if (filters.classId) p.class_id = filters.classId;
      if (filters.sectionId) p.section_id = filters.sectionId;
    }
    return p;
  };

  const buildBulkOptions = () => ({
    academic_year_id: filters.academicYearId,
    exam_id: filters.examId,
    mode: filters.mode,
    template_id: documentType === REPORT_DOCUMENT_TYPES.ID_CARD ? activeIdCardTemplateId : activeTemplateId,
    certificate_fields: {
      purpose: certificateFields.purpose || 'Certificate',
      issue_date: certificateFields.issue_date || todayString(),
      body_text: certificateFields.body_text || '',
    },
    card_format: idCardTemplateMeta.format || filters.cardFormat || 'portrait',
    card_design: idCardTemplateMeta.design || 'saffron',
    sheet_orientation: filters.sheetOrientation,
    include_back: filters.includeBack !== false,
  });

  const sheetPreviewStudents = useMemo(
    () => filtered.filter((s) => selectedIds.includes(s.id)),
    [filtered, selectedIds],
  );

  const handleIdCardTemplateChange = async (id) => {
    setActiveIdCardTemplateId(id);
    const t = findTemplateById(idCardTemplates, id);
    if (t) {
      const { format: fmt } = parseIdCardTemplate(t);
      setFilters((prev) => ({
        ...prev,
        cardFormat: fmt,
      }));
      setPrintSettings((prev) => mergePrintSettings({
        ...prev,
        cardFormat: fmt,
        cardWidthIn: fmt === 'landscape' ? 3 : 2,
        cardHeightIn: fmt === 'landscape' ? 2 : 3,
      }));
    }
    if (!schoolId || !id) return;
    try {
      await api.put('/reports/templates/active', {
        school_id: schoolId,
        category: 'id_card',
        template_id: id,
      });
    } catch (e) {
      toast({
        title: 'Could not save template',
        description: e?.response?.data?.message || 'Try again from Settings',
        variant: 'destructive',
      });
    }
  };

  const handleSavePrintSettings = async (nextSettings) => {
    if (!schoolId) return;
    setSavingPrintSettings(true);
    try {
      const res = await api.put('/reports/id-card/print-settings', {
        school_id: schoolId,
        settings: nextSettings,
      });
      const saved = mergePrintSettings(res.data?.data?.settings || nextSettings);
      setPrintSettings(saved);
      toast({ title: 'Saved', description: 'Print sheet defaults updated for this school.' });
    } catch (e) {
      toast({
        title: 'Save failed',
        description: e?.response?.data?.message || 'Could not save print settings',
        variant: 'destructive',
      });
    } finally {
      setSavingPrintSettings(false);
    }
  };

  const handlePrintSettingsChange = (next) => {
    setPrintSettings(next);
    if (next.cardFormat && next.cardFormat !== filters.cardFormat) {
      setFilters((prev) => ({ ...prev, cardFormat: next.cardFormat }));
    }
  };

  const idCardActionsDisabled = isIdCardPage && (idCardSchoolLoading || !idCardSchool);

  const tcFeeBlocked = (student) => isTcPage && Number(student?.fee_pending ?? 0) > 0.01;

  const tcFeeBlockMessage = (student) => {
    const pending = Number(student?.fee_pending ?? 0);
    return `Cannot generate TC — student has outstanding fee balance of ₹${pending.toLocaleString('en-IN', { minimumFractionDigits: 2 })}. All dues must be paid first.`;
  };

  const downloadPdf = async (student) => {
    if (tcFeeBlocked(student)) {
      toast({ title: 'Fee dues pending', description: tcFeeBlockMessage(student), variant: 'destructive' });
      return;
    }
    if (isIdCardPage) {
      if (idCardSchoolLoading) {
        toast({ title: 'Please wait', description: 'Loading school ID card settings…', variant: 'destructive' });
        return;
      }
      if (!idCardSchool) {
        toast({
          title: 'Download failed',
          description: idCardSchoolError || 'School ID card settings not loaded',
          variant: 'destructive',
        });
        return;
      }
      try {
        const fn = await downloadIdCardPdf(
          student,
          idCardSchool,
          getProfileAssetUrl(student.avatar),
          {
            format: idCardTemplateMeta.format || filters.cardFormat || 'portrait',
            design: idCardTemplateMeta.design || 'saffron',
            imageCacheKey: idCardImageCacheKey(idCardSchool),
          },
        );
        toast({ title: 'Downloaded', description: fn });
      } catch (e) {
        toast({ title: 'Download failed', description: e.message || 'PDF error', variant: 'destructive' });
      }
      return;
    }
    try {
      const res = await api.get(`/reports/${documentType}/pdf`, {
        params: buildParams(student),
        responseType: 'blob',
      });
      await handleBlobDownloadResponse(
        res,
        'pdf',
        `${documentType}-${student.admission_no || student.id}.pdf`,
      );
    } catch (e) {
      toast({ title: 'Download failed', description: e.message || e?.response?.data?.message, variant: 'destructive' });
    }
  };

  const openPreview = (student) => {
    if (tcFeeBlocked(student)) {
      toast({ title: 'Fee dues pending', description: tcFeeBlockMessage(student), variant: 'destructive' });
      return;
    }
    if (isIdCardPage) {
      setIdCardPreviewStudent(student);
      setPreviewOpen(true);
      return;
    }
    setPreviewParams(buildParams(student));
    setPreviewOpen(true);
  };

  const printDoc = async (student) => {
    if (tcFeeBlocked(student)) {
      toast({ title: 'Fee dues pending', description: tcFeeBlockMessage(student), variant: 'destructive' });
      return;
    }
    if (isIdCardPage) {
      if (isNativePlatform()) {
        toast({
          title: 'Print not available',
          description: 'Use Download to save ID cards on mobile.',
          variant: 'destructive',
        });
        return;
      }
      if (idCardSchoolLoading) {
        toast({ title: 'Please wait', description: 'Loading school ID card settings…', variant: 'destructive' });
        return;
      }
      if (!idCardSchool) {
        toast({
          title: 'Print failed',
          description: idCardSchoolError || 'School ID card settings not loaded',
          variant: 'destructive',
        });
        return;
      }
      try {
        await printIdCard(student, idCardSchool, getProfileAssetUrl(student.avatar), {
          format: idCardTemplateMeta.format || filters.cardFormat || 'portrait',
          design: idCardTemplateMeta.design || 'saffron',
          imageCacheKey: idCardImageCacheKey(idCardSchool),
        });
      } catch (e) {
        toast({ title: 'Print failed', description: e?.message || 'Could not print', variant: 'destructive' });
      }
      return;
    }
    if (isNativePlatform()) {
      toast({
        title: 'Print not available',
        description: 'Use Download to save this document on mobile.',
        variant: 'destructive',
      });
      return;
    }
    try {
      const res = await api.get(`/reports/${documentType}/preview`, { params: buildParams(student) });
      const html = res.data?.data?.html || '';
      const w = window.open('', '_blank');
      if (!w) return;
      w.document.write(html);
      w.document.close();
      w.focus();
      w.print();
    } catch (e) {
      toast({ title: 'Print failed', description: e?.response?.data?.message, variant: 'destructive' });
    }
  };

  const tcBulkDisabled = meta.tcBulkOnly && !classIsTwelve;
  const tcBulkReason = tcBulkDisabled ? 'Bulk TC is only available when the selected class is Class 12.' : '';
  const idCardBulkDisabled = isIdCardPage && (idCardSchoolLoading || !idCardSchool);
  const idCardBulkReason = isIdCardPage
    ? (idCardSchoolLoading ? 'Loading school ID card settings…' : idCardSchoolError)
    : '';

  const handleBulk = async (output) => {
    if (isTcPage) {
      const blocked = filtered.filter((s) => selectedIds.includes(s.id) && tcFeeBlocked(s));
      if (blocked.length > 0) {
        toast({
          title: 'Fee dues pending',
          description: `${blocked.length} selected student(s) have outstanding fees. All dues must be paid before bulk TC download.`,
          variant: 'destructive',
        });
        return;
      }
    }
    if (meta.tcBulkOnly && tcBulkDisabled) {
      toast({ title: 'Not allowed', description: tcBulkReason, variant: 'destructive' });
      return;
    }
    if (isIdCardPage && idCardSchoolLoading) {
      toast({ title: 'Please wait', description: 'Loading school ID card settings…', variant: 'destructive' });
      return;
    }
    if (isIdCardPage && !idCardSchool) {
      toast({
        title: 'Bulk failed',
        description: idCardSchoolError || 'School ID card settings not loaded',
        variant: 'destructive',
      });
      return;
    }
    try {
      await runBulk({
        studentIds: selectedIds,
        students: filtered,
        school: isIdCardPage ? idCardSchool : null,
        options: buildBulkOptions(),
        printSettings: isIdCardPage ? mergePrintSettings({
          ...printSettings,
          cardFormat: idCardTemplateMeta.format || filters.cardFormat || 'portrait',
        }) : undefined,
        output,
        classId: filters.classId || undefined,
        sectionId: filters.sectionId || undefined,
        schoolId,
      });
      toast({ title: 'Bulk download complete' });
      clearSelection();
    } catch (e) {
      toast({ title: 'Bulk failed', description: e.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{meta.title}</h1>
        <p className="text-muted-foreground mt-1">{meta.description}</p>
      </div>

      <ReportFiltersBar
        schoolId={schoolId}
        filters={filters}
        onChange={setFilters}
        showExamMode={meta.showExamMode}
        showCertificateFields={meta.showCertificateFields}
        showCardFormat={meta.showCardFormat}
        showIdCardBulkOptions={documentType === REPORT_DOCUMENT_TYPES.ID_CARD}
        idCardTemplates={idCardTemplates}
        activeIdCardTemplateId={activeIdCardTemplateId}
        onIdCardTemplateChange={handleIdCardTemplateChange}
        allowAllClasses={allowAllClasses}
        certificateFields={certificateFields}
        onCertificateFieldsChange={setCertificateFields}
      />

      {error && <p className="text-sm text-red-500">{error}</p>}
      {needsClass && !filters.classId && (
        <p className="text-sm text-muted-foreground">Select a class to load students.</p>
      )}

      {isIdCardPage && idCardSchoolLoading && (
        <p className="text-sm text-muted-foreground">Loading school ID card settings…</p>
      )}
      {isIdCardPage && idCardSchoolError && !idCardSchoolLoading && (
        <p className="text-sm text-red-500">{idCardSchoolError}</p>
      )}

      {isIdCardPage && (
        <IdCardPrintSettingsPanel
          settings={printSettings}
          onChange={handlePrintSettingsChange}
          onSave={handleSavePrintSettings}
          saving={savingPrintSettings}
        />
      )}

      <StudentSelectTable
        students={filtered}
        loading={loading}
        search={search}
        onSearchChange={setSearch}
        selectedIds={selectedIds}
        allSelected={allSelected}
        onToggleAll={toggleAll}
        onToggleOne={toggleOne}
        onPreview={openPreview}
        onPdf={downloadPdf}
        onPrint={printDoc}
        pdfPrintDisabled={idCardActionsDisabled}
        showTcEdit={isTcPage}
        showFeePending={isTcPage}
        isActionDisabled={(s) => tcFeeBlocked(s)}
        actionDisabledTitle="Outstanding fee balance — pay all dues before generating TC"
        onEditTc={(s) => { setTcStudent(s); setTcOpen(true); }}
      />

      {canBulk && (
        <BulkDownloadPanel
          selectedCount={selectedIds.length}
          bulkLabel={meta.bulkLabel}
          disabled={tcBulkDisabled || idCardBulkDisabled}
          disabledReason={idCardBulkReason || tcBulkReason || bulkError}
          running={running}
          progress={progress}
          onConfirmBulk={handleBulk}
          isIdCard={documentType === REPORT_DOCUMENT_TYPES.ID_CARD}
          canPreviewSheet={documentType === REPORT_DOCUMENT_TYPES.ID_CARD}
          onPreviewSheet={() => {
            if (selectedIds.length === 0) {
              toast({ title: 'Select students', description: 'Choose at least one student for bulk preview.', variant: 'destructive' });
              return;
            }
            if (selectedIds.length > 50) {
              toast({ title: 'Too many students', description: 'Bulk preview supports up to 50 students at a time.', variant: 'destructive' });
              return;
            }
            setSheetPreviewOpen(true);
          }}
        />
      )}

      {isIdCardPage ? (
        <IdCardPreviewModal
          open={previewOpen}
          onOpenChange={(open) => {
            setPreviewOpen(open);
            if (!open) setIdCardPreviewStudent(null);
          }}
          student={idCardPreviewStudent}
          schoolId={schoolId}
          format={idCardTemplateMeta.format || filters.cardFormat || 'portrait'}
          design={idCardTemplateMeta.design || 'saffron'}
        />
      ) : (
        <DocumentPreviewModal
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          documentType={documentType}
          params={previewParams}
          title={`${meta.title} Preview`}
        />
      )}

      {isIdCardPage && (
        <IdCardSheetPreviewModal
          open={sheetPreviewOpen}
          onOpenChange={setSheetPreviewOpen}
          students={sheetPreviewStudents}
          schoolId={schoolId}
          format={idCardTemplateMeta.format || filters.cardFormat || 'portrait'}
          design={idCardTemplateMeta.design || 'saffron'}
          printSettings={printSettings}
        />
      )}

      <TcEditModal
        open={tcOpen}
        onOpenChange={setTcOpen}
        student={tcStudent}
        onSaved={refetch}
      />
    </div>
  );
}
